import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { FiSearch } from 'react-icons/fi';
import usePermission from '@/hooks/usePermission';
import { confirm } from '@/lib/confirm';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Filter, FilterContent, FilterActions, FilterInput, FilterSelectNative, FilterCheck } from '@/components/custom/filter';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ATIVIDADES } from '@/pages/dashboard/home-teacher/teacher-character';
import { isPendingEvaluationStatus } from '../activityStatusVariant';
import ActivityForm from '../ActivityForm';
import { ComplementaryActivityRowTeacher } from './ComplementaryActivityRowTeacher';

// Criar/editar/excluir batem na API real, igual ao tema Default — reaproveita
// o mesmo ActivityForm.jsx (sem nenhum ajuste nos campos) dentro do mesmo
// Dialog, no lugar do modal próprio que existia antes (ActivityFormModal.jsx,
// removido). O estado de atividades deixou de viver num store compartilhado:
// agora é local, igual ao Default/index.jsx, já que cada tela busca seus
// próprios dados da API.
//
// `adaptApiActivity` só serve pra exibição da linha (ComplementaryActivityRowTeacher);
// filtros e o form de criar/editar continuam operando no formato cru da API,
// igual ao Default (ver filterActivitiesByCourse/Classroom e isPendingEvaluationStatus
// em Default/index.jsx).
function scopeFor(activity) {
  if (activity['is-public']) return 'publico';
  if (activity['classroom-id']) return 'turma';
  if (activity['course-id']) return 'curso';
  return 'publico';
}

function pendingEvaluationLabel(activity) {
  const pendingCount = Number(activity['activity-status-or-submitted-count']) || 0;
  return pendingCount > 0 ? `${pendingCount} para avaliar` : null;
}

function adaptApiActivity(activity) {
  return {
    id: activity.id,
    title: activity.title,
    scope: scopeFor(activity),
    classHours: activity['class-hours'],
    tags: (activity.taxonomies || []).map(taxonomy => taxonomy.name).filter(Boolean),
    statusLabel: pendingEvaluationLabel(activity)
  };
}

function filterActivitiesByCourse(id, activities) {
  return activities.filter(activity => activity['course-id'] === id);
}

function filterActivitiesByClassroom(id, activities) {
  return activities.filter(activity => activity['classroom-id'] === id);
}

export default function ComplementaryActivityPlayfulTeacher({ uri }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const client = useClient();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('blank');
  const [selectedClassroom, setSelectedClassroom] = useState('blank');
  const [onlyPendingEvaluation, setOnlyPendingEvaluation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState();
  const [relationships, setRelationships] = useState({ courses: [], classrooms: [] });

  const canCreate = usePermission('Criar atividades', 'Arquivos e atividades complementares');

  const getActivities = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('activities');
    if (error) {
      toast.error(t('toast.errorFetchComplementActivity'));
    } else {
      setActivities(data || []);
    }
    setLoading(false);
  };

  const getRelationships = async () => {
    const { data: courses } = await client.fetch('courses');
    const { data: classrooms } = await client.fetch('classrooms');
    setRelationships({ courses: courses || [], classrooms: classrooms || [] });
  };

  useEffect(() => {
    getActivities();
    getRelationships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const courseOptions = useMemo(() => relationships.courses.map(course => ({ id: course.id, name: course.title })), [relationships.courses]);
  const classroomOptions = useMemo(() => relationships.classrooms.map(classroom => ({ id: classroom.id, name: classroom.title })), [relationships.classrooms]);

  let filteredActivities = activities;
  if (selectedCourse !== 'blank') filteredActivities = filterActivitiesByCourse(+selectedCourse, filteredActivities);
  if (selectedClassroom !== 'blank') filteredActivities = filterActivitiesByClassroom(+selectedClassroom, filteredActivities);
  if (searchTerm !== '') filteredActivities = filteredActivities.filter(activity => activity.title != null && activity.title.toLowerCase().includes(searchTerm.toLowerCase()));
  if (onlyPendingEvaluation) filteredActivities = filteredActivities.filter(activity => isPendingEvaluationStatus(activity['activity-status-or-submitted-count'], t));

  const deleteActivity = async id => {
    setLoading(true);
    const { error } = await client.delete(['activities', id]);
    if (error) {
      toast.error(t('toast.errorDeleteActivity'));
      setLoading(false);
    } else {
      getActivities();
      toast.success(t('toast.successDeleteActivity'));
    }
  };

  const handleDeleteActivity = async activity => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteActivity'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });

    if (confirmed) deleteActivity(activity.id);
  };

  const newActivity = () => {
    setEditingActivity({});
    setShowModal(true);
  };

  const editActivity = activity => {
    setEditingActivity(activity);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingActivity(null);
  };

  const breadCrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Atividade Complementar',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadCrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={ATIVIDADES}
        imageAlt="Personagem do professor"
        imageHeight={200}
        imageOffset={260}
        bubbleCorner="tl"
        bubbleMaxWidth={420}
        text="Suas atividades complementares estão aqui! Crie novas, acompanhe os envios e avalie as respostas dos estudantes. 📘"
      />

      <Filter variant="plain">
        <FilterContent>
          <FilterInput
            label="Buscar"
            endIcon={FiSearch}
            placeholder="Buscar atividade"
            type="search"
            name="activitySearch"
            id="activitySearch"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <FilterSelectNative
            label="Filtrar por curso"
            value={selectedCourse}
            onChange={e => setSelectedCourse(e.target.value)}
            blankLabel="Todos os cursos"
            options={courseOptions}
          />

          <FilterSelectNative
            label="Filtrar por turma"
            value={selectedClassroom}
            onChange={e => setSelectedClassroom(e.target.value)}
            blankLabel="Todas as turmas"
            options={classroomOptions}
          />

          <FilterCheck
            label="Pendente de avaliação"
            name="pending-evaluation"
            id="pending-evaluation"
            checked={onlyPendingEvaluation}
            onCheckedChange={checked => setOnlyPendingEvaluation(checked)}
          />
        </FilterContent>

        {canCreate && (
          <FilterActions>
            <PlayfulCardButton
              className="w-auto px-6"
              onClick={newActivity}
            >
              Nova atividade
            </PlayfulCardButton>
          </FilterActions>
        )}
      </Filter>

      {loading && <Loader />}

      {!loading && filteredActivities.length === 0 && (
        <div className="flex min-h-[50vh] items-center justify-center">
          <EmptyState type="data" />
        </div>
      )}

      {!loading && filteredActivities.length > 0 && (
        <div className="relative flex flex-col gap-3">
          {filteredActivities.map((activity, index) => (
            <ComplementaryActivityRowTeacher
              key={activity.id}
              activity={adaptApiActivity(activity)}
              index={index}
              onEdit={() => editActivity(activity)}
              onDelete={() => handleDeleteActivity(activity)}
              onEvaluate={() => navigate(`/atividade-complementar/avaliar-atividades/${activity.id}`)}
            />
          ))}
        </div>
      )}

      <Dialog
        open={showModal}
        onOpenChange={setShowModal}
      >
        <DialogContent>
          <DialogTitle className="sr-only">{editingActivity?.id ? 'Editar atividade' : 'Nova atividade'}</DialogTitle>
          <ActivityForm
            onClose={closeModal}
            editingActivity={editingActivity}
            activities={activities}
            setActivities={setActivities}
          />
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
